import React, { useState, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useSpeechDetection } from '../../../hooks/useSpeechDetection';
import { useAudioProcessing } from '../../../hooks/useAudioProcessing';
import { useChatContext } from '../../../contexts/ChatContext';
import { useTutor } from '../../../contexts/TutorContext';
import { useAudioState } from '../../../contexts/AudioContext';
import { styles } from './AutoDetect.styles';
import { AudioVisualizer } from './AudioVisualizer';
import { MessageSlider } from './MessageSlider';

interface AutoDetectProps {
  sessionId: string;
  tutorId: string;
  onResponse: (text: string) => void;
  onError: (error: string) => void;
  isReady: boolean;
  showMessages: boolean;
}

export const AutoDetect: React.FC<AutoDetectProps> = ({
  sessionId,
  tutorId,
  onResponse,
  onError,
  isReady,
  showMessages,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const isReadyRef = useRef(isReady);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  const { addMessage, setIsWaitingForAI, messages } = useChatContext();
  const { selectedTutor } = useTutor();
  const { setVisualizerState, visualizerState } = useAudioState();

  const { sendAudioToApi, audioUrl } = useAudioProcessing({
    sessionId,
    tutorId,
    languageCode: selectedTutor?.language_code || 'en',
    voiceId: selectedTutor?.voice_id || '',
    onResponse,
    onError,
    setVisualizerState,
    addMessage,
    setIsWaitingForAI
  });

  useEffect(() => {
    isReadyRef.current = isReady;
  }, [isReady]);

  const setupMediaRecorder = (stream: MediaStream) => {
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm',
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current = mediaRecorder;
  };

  const { isListening, stream } = useSpeechDetection({
    onSpeechStart: () => {
      if (!isReadyRef.current) return;
      console.log('Speech started');
      setIsProcessing(false);
      audioChunksRef.current = [];
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.start();
      }
    },
    onSpeechEnd: async () => {
      if (!isReadyRef.current) return;
      console.log('Speech ended, setting state to waiting');
      setIsProcessing(true);
      setVisualizerState('waiting');
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await sendAudioToApi(audioBlob);
          setIsProcessing(false);
        };
      }
    },
    onError: (error) => {
      console.error('Speech detection error:', error);
      setIsProcessing(false);
      setInitialized(false);
      onError(error);
    },
    enabled: isReady && visualizerState !== 'speaking' && visualizerState !== 'waiting'
  });

  useEffect(() => {
    console.log('Current visualizer state:', visualizerState);
  }, [visualizerState]);

  useEffect(() => {
    // console.log('Speech detection state:', { isReady, visualizerState, isListening });
  }, [isReady, visualizerState, isListening]);

  useEffect(() => {
    if (stream) {
      setInitialized(true);
      setupMediaRecorder(stream);
      setVisualizerState('listening');
    } else {
      setInitialized(false);
    }
  }, [stream]);

  useEffect(() => {
    return () => {
      setInitialized(false);
      setIsProcessing(false);
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.visualizerContainer}>
        <AudioVisualizer 
          isRecording={isListening || visualizerState === 'speaking'}
          mode={visualizerState}
          audioUrl={audioUrl}
        />
      </View>
      {showMessages && messages.length > 0 && (
        <View style={styles.messagesContainer}>
          <MessageSlider 
            messages={messages} 
            isTTSPlaying={visualizerState === 'speaking'}
          />
        </View>
      )}
    </View>
  );
};
